import { bindCalculatorAssignmentConnections } from "./calculator-assignment-connections.js";
import {
    calculatorLootEditorFocusedControl,
    restoreCalculatorLootEditorFocusedControl,
} from "./calculator-loot-table-editor-interactions.js";
import {
    renderCalculatorLayerInspectorRankingPresentation,
    renderCalculatorLayerInspectorRegion,
} from "./calculator-layer-inspector-view.js";
import {
    renderCalculatorLootTableEditorAvailableList,
    renderCalculatorLootTableEditorGroupContent,
} from "./calculator-loot-table-editor-view.js";
import {
    bindProvenanceDetailBindings,
    createProvenanceDetailBindings,
} from "./provenance-detail-bindings.js";
import { calculatorFishingSourcesEqual } from "./calculator-fishing-source.js";

const PROJECTION_SORTS = new Set([
    "droprate",
    "expected_value",
    "exp",
    "totem_exp",
    "evidence",
]);

function layerHeaderKey(document) {
    const layer = document?.userLayer;
    const provenance = layer?.provenance;
    return JSON.stringify([
        document?.documentVersion,
        layer?.layerId,
        layer?.name,
        layer?.order,
        layer?.evidenceClass,
        provenance?.sourceId,
        provenance?.sourceRevision,
        provenance?.importedAtUtc,
    ]);
}

function operationMap(document) {
    const operations = new Map();
    for (const operation of document?.userLayer?.operations ?? []) {
        const operationId = operation?.operationId;
        if (typeof operationId !== "string" || !operationId || operations.has(operationId)) {
            return null;
        }
        operations.set(operationId, operation);
    }
    return operations;
}

function changedOperations(previousDocument, nextDocument) {
    const previous = operationMap(previousDocument);
    const next = operationMap(nextDocument);
    if (!previous || !next) {
        return null;
    }
    const changed = [];
    for (const operationId of new Set([...previous.keys(), ...next.keys()])) {
        const before = previous.get(operationId) ?? null;
        const after = next.get(operationId) ?? null;
        if (JSON.stringify(before) !== JSON.stringify(after)) {
            if (before) {
                changed.push(before);
            }
            if (after) {
                changed.push(after);
            }
        }
    }
    return changed;
}

function groupIdsEqual(previous, next) {
    return previous.length === next.length
        && previous.every((groupId, index) => groupId === next[index]);
}

function groupsForItem(states, itemId) {
    const groups = new Set();
    for (const state of states) {
        for (const [groupId, assignments] of state.editor.assignmentsByGroup) {
            if (assignments.some((assignment) => assignment.itemId === itemId)) {
                groups.add(groupId);
            }
        }
        for (const trace of state.projection.provenanceTraces ?? []) {
            if (
                trace.factKey?.kind === "groupMembership"
                && trace.factKey.itemId === itemId
                && typeof trace.factKey.groupId === "string"
            ) {
                groups.add(trace.factKey.groupId);
            }
        }
    }
    return groups;
}

function factKeyForFactId(states, targetFactId) {
    const keys = new Map();
    for (const state of states) {
        for (const trace of state.projection.provenanceTraces ?? []) {
            if ((trace.candidates ?? []).some((candidate) => (
                candidate.operation?.factId === targetFactId
            ))) {
                const key = JSON.stringify(trace.factKey);
                keys.set(key, trace.factKey);
            }
        }
    }
    return keys.size === 1 ? keys.values().next().value : null;
}

function projectionSortActive(lootSort) {
    return PROJECTION_SORTS.has(lootSort.by);
}

function addItemGroups(impact, states, itemId) {
    for (const groupId of groupsForItem(states, itemId)) {
        impact.groupIds.add(groupId);
    }
}

function classifyFactKey(impact, factKey, states, lootSort) {
    switch (factKey?.kind) {
    case "itemQuantityDefault":
        if (!Number.isInteger(factKey.itemId)) {
            return false;
        }
        addItemGroups(impact, states, factKey.itemId);
        impact.catalog = true;
        return true;
    case "itemQuantity":
        if (typeof factKey.groupId !== "string") {
            return false;
        }
        impact.groupIds.add(factKey.groupId);
        return true;
    case "itemRate":
        if (typeof factKey.groupId !== "string") {
            return false;
        }
        impact.groupIds.add(factKey.groupId);
        impact.catalog ||= projectionSortActive(lootSort);
        return true;
    case "groupRate":
        for (const groupId of states[1].editor.groupIds) {
            impact.groupIds.add(groupId);
        }
        impact.catalog ||= projectionSortActive(lootSort);
        return true;
    case "groupMembership":
        if (typeof factKey.groupId !== "string") {
            return false;
        }
        impact.groupIds.add(factKey.groupId);
        impact.catalog = true;
        impact.ranking = true;
        return true;
    case "itemPresence":
        if (!Number.isInteger(factKey.itemId)) {
            return false;
        }
        addItemGroups(impact, states, factKey.itemId);
        impact.catalog = true;
        impact.ranking = true;
        return true;
    case "itemPrice":
        if (!Number.isInteger(factKey.itemId)) {
            return false;
        }
        addItemGroups(impact, states, factKey.itemId);
        impact.catalog = true;
        return true;
    default:
        return false;
    }
}

function classifyOperation(impact, operation, states, lootSort, fishingSource) {
    if (
        operation.fishingSource !== undefined
        && !calculatorFishingSourcesEqual(operation.fishingSource, fishingSource)
    ) {
        return true;
    }
    if (operation.kind === "refuteEvidence") {
        const factKey = factKeyForFactId(states, operation.targetFactId);
        if (
            factKey?.fishingSource !== undefined
            && !calculatorFishingSourcesEqual(factKey.fishingSource, fishingSource)
        ) {
            return true;
        }
        return factKey
            ? classifyFactKey(impact, factKey, states, lootSort)
            : false;
    }
    return classifyFactKey(impact, operation, states, lootSort);
}

/**
 * Classifies the exact keyed editor regions that a resolved projection update
 * can safely repaint. A null result requires a full declarative render.
 */
export function calculatorLayerInspectorProjectionImpact({
    previousState,
    nextState,
    previousDocument,
    nextDocument,
    lootSort,
}) {
    if (!previousState || !nextState) {
        return null;
    }
    if (!groupIdsEqual(previousState.editor.groupIds, nextState.editor.groupIds)) {
        return null;
    }
    const states = [previousState, nextState];
    const documentChanged = previousDocument !== nextDocument;
    const impact = {
        groupIds: new Set(),
        catalog: false,
        ranking: previousState.rankingEvidenceKey !== nextState.rankingEvidenceKey,
        operations: documentChanged,
        layers: documentChanged
            || previousState.effectiveLayerId !== nextState.effectiveLayerId,
        advanced: previousState.advancedEditor !== nextState.advancedEditor,
        provenance: previousState.provenanceGraphKey !== nextState.provenanceGraphKey,
    };
    if (!documentChanged) {
        for (const groupId of nextState.editor.groupIds) {
            impact.groupIds.add(groupId);
        }
        impact.catalog = projectionSortActive(lootSort);
        return impact;
    }
    if (layerHeaderKey(previousDocument) !== layerHeaderKey(nextDocument)) {
        return null;
    }
    const operations = changedOperations(previousDocument, nextDocument);
    if (!operations?.length) {
        return null;
    }
    for (const operation of operations) {
        if (!classifyOperation(
            impact,
            operation,
            states,
            lootSort,
            nextState.model.selection.fishingSource,
        )) {
            return null;
        }
    }
    return impact;
}

function uniqueElement(root, selector) {
    const elements = [...(root.querySelectorAll?.(selector) ?? [])];
    return elements.length === 1 ? elements[0] : null;
}

function keyedGroupElements(host, expectedGroupIds) {
    const groups = new Map();
    for (const element of host.querySelectorAll?.("[data-loot-editor-group]") ?? []) {
        const groupId = element.getAttribute?.("data-loot-editor-group") ?? "";
        if (!groupId || groups.has(groupId)) {
            return null;
        }
        groups.set(groupId, element);
    }
    return groupIdsEqual([...groups.keys()], expectedGroupIds) ? groups : null;
}

function captureOpenDetails(region) {
    return [...(region?.querySelectorAll?.("details") ?? [])]
        .map((details) => Boolean(details.open));
}

function restoreOpenDetails(region, states) {
    [...(region?.querySelectorAll?.("details") ?? [])].forEach((details, index) => {
        details.open = states[index] ?? details.open;
    });
}

function provenanceScrollerKey(scroller, index) {
    const channel = scroller.closest?.("[data-provenance-channel]")
        ?.getAttribute?.("data-provenance-channel");
    return channel ? `channel:${channel}` : `index:${index}`;
}

function captureProvenanceScroll(region) {
    return new Map([
        ...(region?.querySelectorAll?.("[data-loot-provenance-graph-scroller]") ?? []),
    ].map((scroller, index) => [
        provenanceScrollerKey(scroller, index),
        Object.freeze({
            left: Number.isFinite(scroller.scrollLeft) ? scroller.scrollLeft : 0,
            top: Number.isFinite(scroller.scrollTop) ? scroller.scrollTop : 0,
        }),
    ]));
}

/**
 * Restores captured offsets onto a freshly rendered scroller. Zero is already
 * its native initial state and assigning it can force synchronous layout.
 */
export function restoreCalculatorLayerInspectorScroll(scroller, scroll) {
    if (scroll.left !== 0) {
        scroller.scrollLeft = scroll.left;
    }
    if (scroll.top !== 0) {
        scroller.scrollTop = scroll.top;
    }
}

function restoreProvenanceScroll(region, states) {
    [...(region?.querySelectorAll?.("[data-loot-provenance-graph-scroller]") ?? [])]
        .forEach((scroller, index) => {
            const scroll = states.get(provenanceScrollerKey(scroller, index));
            if (scroll !== undefined) {
                restoreCalculatorLayerInspectorScroll(scroller, scroll);
            }
        });
}

function copyElementAttributes(target, source) {
    for (const attribute of [...(target?.attributes ?? [])]) {
        target.removeAttribute(attribute.name);
    }
    for (const attribute of [...(source?.attributes ?? [])]) {
        target.setAttribute(attribute.name, attribute.value);
    }
}

function copyElementAttributesExcept(target, source, preservedNames) {
    const sourceNames = new Set(
        [...(source?.attributes ?? [])].map((attribute) => attribute.name),
    );
    for (const attribute of [...(target?.attributes ?? [])]) {
        if (!preservedNames.has(attribute.name) && !sourceNames.has(attribute.name)) {
            target.removeAttribute(attribute.name);
        }
    }
    for (const attribute of [...(source?.attributes ?? [])]) {
        if (
            !preservedNames.has(attribute.name)
            && target.getAttribute?.(attribute.name) !== attribute.value
        ) {
            target.setAttribute(attribute.name, attribute.value);
        }
    }
}

/**
 * Refreshes the declarative rate-form projection around its live text input.
 * The input and both of its ancestors stay connected so native caret and IME
 * state survive, while every other rendered rate-form node comes from the
 * confirmed projection.
 */
export function reconcileCalculatorLootFocusedRateForm(
    currentForm,
    nextForm,
    activeInput,
) {
    const currentLegend = currentForm?.querySelector?.(":scope > legend");
    const nextLegend = nextForm?.querySelector?.(":scope > legend");
    const currentProjections = currentForm?.querySelector?.(
        ":scope > [data-loot-editor-rate-projections]",
    );
    const nextProjections = nextForm?.querySelector?.(
        ":scope > [data-loot-editor-rate-projections]",
    );
    const currentControls = currentForm?.querySelector?.(
        ":scope > [data-loot-editor-rate-controls]",
    );
    const nextControls = nextForm?.querySelector?.(
        ":scope > [data-loot-editor-rate-controls]",
    );
    const currentTablist = currentControls?.querySelector?.(":scope > [role=\"tablist\"]");
    const nextTablist = nextControls?.querySelector?.(":scope > [role=\"tablist\"]");
    const currentInput = currentControls?.querySelector?.(
        ":scope > [data-loot-editor-rate-value]",
    );
    const nextInput = nextControls?.querySelector?.(
        ":scope > [data-loot-editor-rate-value]",
    );
    const currentFormChildren = [...(currentForm?.children ?? [])];
    const nextFormChildren = [...(nextForm?.children ?? [])];
    const currentControlChildren = [...(currentControls?.children ?? [])];
    const nextControlChildren = [...(nextControls?.children ?? [])];
    if (
        !currentLegend
        || !nextLegend
        || !currentProjections
        || !nextProjections
        || !currentControls
        || !nextControls
        || !currentTablist
        || !nextTablist
        || currentInput !== activeInput
        || !nextInput
        || currentForm.getAttribute?.("data-rate-scope")
            !== nextForm.getAttribute?.("data-rate-scope")
        || currentForm.getAttribute?.("data-group-id")
            !== nextForm.getAttribute?.("data-group-id")
        || currentForm.getAttribute?.("data-item-id")
            !== nextForm.getAttribute?.("data-item-id")
        || currentFormChildren.length !== 3
        || currentFormChildren[0] !== currentLegend
        || currentFormChildren[1] !== currentProjections
        || currentFormChildren[2] !== currentControls
        || nextFormChildren.length !== 3
        || nextFormChildren[0] !== nextLegend
        || nextFormChildren[1] !== nextProjections
        || nextFormChildren[2] !== nextControls
        || currentControlChildren.length !== 2
        || currentControlChildren[0] !== currentTablist
        || currentControlChildren[1] !== activeInput
        || nextControlChildren.length !== 2
        || nextControlChildren[0] !== nextTablist
        || nextControlChildren[1] !== nextInput
    ) {
        return false;
    }

    copyElementAttributes(currentForm, nextForm);
    copyElementAttributes(currentControls, nextControls);
    copyElementAttributesExcept(activeInput, nextInput, new Set(["value"]));
    currentLegend.replaceWith(nextLegend);
    currentProjections.replaceWith(nextProjections);
    currentTablist.replaceWith(nextTablist);
    return true;
}

function directChildWithin(element, parent) {
    let child = element;
    while (child && child.parentElement !== parent) {
        child = child.parentElement;
    }
    return child?.parentElement === parent ? child : null;
}

function reconcileProjectedChildren(
    currentParent,
    nextParent,
    currentPreserved,
    nextPreserved,
) {
    const desired = [...nextParent.children].map((child) => (
        child === nextPreserved ? currentPreserved : child
    ));
    let cursor = currentParent.firstElementChild;
    for (const child of desired) {
        if (child !== cursor) {
            currentParent.insertBefore(child, cursor);
        }
        cursor = child.nextElementSibling;
    }
    const retained = new Set(desired);
    for (const child of [...currentParent.children]) {
        if (!retained.has(child)) {
            child.remove();
        }
    }
}

/**
 * Applies a new declarative group projection without replacing the focused
 * rate fieldset. Keeping that input connected preserves native focus, caret,
 * and IME state and avoids a forced focus/layout cycle on every digit.
 */
function reconcileFocusedRateGroupContent(group, markup) {
    const documentRef = group?.ownerDocument;
    const active = documentRef?.activeElement;
    if (
        !active?.matches?.("[data-loot-editor-rate-value]")
        || !group.contains?.(active)
        || typeof documentRef.createElement !== "function"
    ) {
        return false;
    }
    const template = documentRef.createElement("template");
    template.innerHTML = markup;
    const nextHeader = template.content.querySelector(
        "[data-loot-editor-group-header]",
    );
    const nextList = template.content.querySelector(
        "[data-loot-editor-group-list]",
    );
    const currentHeader = group.querySelector(
        ":scope > [data-loot-editor-group-header]",
    );
    const currentList = group.querySelector(
        ":scope > [data-loot-editor-group-list]",
    );
    if (!nextHeader || !nextList || !currentHeader || !currentList) {
        return false;
    }

    const activeItemRow = active.closest?.("[data-loot-editor-group-item]");
    if (activeItemRow) {
        const itemId = activeItemRow.getAttribute("data-loot-editor-group-item");
        const nextActiveRow = [
            ...nextList.querySelectorAll("[data-loot-editor-group-item]"),
        ].find((row) => (
            row.getAttribute("data-loot-editor-group-item") === itemId
        ));
        const currentTopChild = directChildWithin(activeItemRow, currentList);
        const nextTopChild = directChildWithin(nextActiveRow, nextList);
        const currentNested = currentTopChild !== activeItemRow;
        const nextNested = nextTopChild !== nextActiveRow;
        if (
            !currentTopChild
            || !nextTopChild
            || currentNested !== nextNested
            || (
                currentNested
                && (
                    !currentTopChild.matches?.("[data-loot-editor-absent-assignments]")
                    || !nextTopChild.matches?.("[data-loot-editor-absent-assignments]")
                    || !activeItemRow.parentElement?.matches?.(
                        "[data-loot-editor-absent-list]",
                    )
                    || !nextActiveRow.parentElement?.matches?.(
                        "[data-loot-editor-absent-list]",
                    )
                )
            )
        ) {
            return false;
        }
        const currentVisual = activeItemRow.querySelector(
            "[data-fishy-loot-row]",
        );
        const nextVisual = nextActiveRow?.querySelector(
            "[data-fishy-loot-row]",
        );
        const currentRateForm = active.closest?.(
            '[data-loot-editor-rate-form][data-rate-scope="item"]',
        );
        const nextRateForm = nextActiveRow?.querySelector?.(
            '[data-loot-editor-rate-form][data-rate-scope="item"]',
        );
        if (
            !nextActiveRow
            || !currentVisual
            || !nextVisual
            || !reconcileCalculatorLootFocusedRateForm(
                currentRateForm,
                nextRateForm,
                active,
            )
        ) {
            return false;
        }
        copyElementAttributes(activeItemRow, nextActiveRow);
        currentVisual.replaceWith(nextVisual);

        if (currentNested) {
            reconcileProjectedChildren(
                activeItemRow.parentElement,
                nextActiveRow.parentElement,
                activeItemRow,
                nextActiveRow,
            );
        }
        reconcileProjectedChildren(
            currentList,
            nextList,
            currentTopChild,
            nextTopChild,
        );
    } else {
        const activeGroupForm = active.closest?.(
            '[data-loot-editor-rate-form][data-rate-scope="group"]',
        );
        if (!activeGroupForm || activeGroupForm.parentElement !== group) {
            return false;
        }
        const nextGroupForm = template.content.querySelector(
            '[data-loot-editor-rate-form][data-rate-scope="group"]',
        );
        if (!reconcileCalculatorLootFocusedRateForm(
            activeGroupForm,
            nextGroupForm,
            active,
        )) {
            return false;
        }
        currentList.replaceWith(nextList);
    }
    currentHeader.replaceWith(nextHeader);
    return true;
}

function focusScope(host) {
    const active = host.ownerDocument?.activeElement;
    if (!active || !host.contains?.(active)) {
        return null;
    }
    const group = active.closest?.("[data-loot-editor-group]");
    if (group) {
        return { kind: "group", key: group.getAttribute("data-loot-editor-group") };
    }
    if (active.closest?.("[data-loot-editor-available-list]")) {
        return { kind: "catalog", key: null };
    }
    if (active.closest?.("[data-layer-inspector-ranking-body]")) {
        return { kind: "ranking", key: null };
    }
    const region = active.closest?.("[data-layer-inspector-region]");
    return region
        ? { kind: "region", key: region.getAttribute("data-layer-inspector-region") }
        : null;
}

function focusRoot(scope, targets) {
    if (scope?.kind === "group") {
        return targets.groups.get(scope.key) ?? null;
    }
    if (scope?.kind === "catalog") {
        return targets.catalog;
    }
    if (scope?.kind === "ranking") {
        return targets.rankingBody;
    }
    if (scope?.kind === "region") {
        return targets.regions.get(scope.key) ?? null;
    }
    return null;
}

function regionNames(impact) {
    return ["provenance", "layers", "operations", "advanced"]
        .filter((region) => impact[region]);
}

/**
 * Applies a fully validated regional plan. No host-level innerHTML assignment
 * occurs and the assignment-connections custom element remains mounted.
 */
export function reconcileCalculatorLayerInspectorProjection({
    host,
    previousState,
    nextState,
    previousDocument,
    nextDocument,
    lootSort,
    announcement,
}) {
    const impact = calculatorLayerInspectorProjectionImpact({
        previousState,
        nextState,
        previousDocument,
        nextDocument,
        lootSort,
    });
    if (!impact || typeof host?.querySelectorAll !== "function") {
        return false;
    }

    const groups = keyedGroupElements(host, nextState.editor.groupIds);
    const workspace = uniqueElement(host, "[data-loot-editor-workspace]");
    const announcementElement = uniqueElement(host, "[data-layer-inspector-announcement]");
    const catalog = impact.catalog
        ? uniqueElement(host, "[data-loot-editor-available-list]")
        : null;
    const catalogSummary = impact.catalog
        ? uniqueElement(host, "[data-loot-editor-available-summary]")
        : null;
    const rankingBody = impact.ranking
        ? uniqueElement(host, "[data-layer-inspector-ranking-body]")
        : null;
    const rankingSummary = impact.ranking
        ? uniqueElement(host, "[data-layer-inspector-ranking-summary]")
        : null;
    const regions = new Map();
    for (const region of regionNames(impact)) {
        const element = uniqueElement(
            host,
            `[data-layer-inspector-region="${region}"]`,
        );
        if (!element) {
            return false;
        }
        regions.set(region, element);
    }
    if (
        !groups
        || !workspace
        || !announcementElement
        || (impact.catalog && (!catalog || !catalogSummary))
        || (impact.ranking && (!rankingBody || !rankingSummary))
        || [...impact.groupIds].some((groupId) => !groups.has(groupId))
    ) {
        return false;
    }

    const groupFragments = new Map();
    for (const groupId of impact.groupIds) {
        const detailBindings = createProvenanceDetailBindings();
        groupFragments.set(groupId, {
            detailBindings,
            markup: renderCalculatorLootTableEditorGroupContent(
                nextState.editor,
                groupId,
                { provenanceDetailBindings: detailBindings },
            ),
        });
    }
    let catalogFragment = null;
    if (impact.catalog) {
        const detailBindings = createProvenanceDetailBindings();
        catalogFragment = {
            detailBindings,
            markup: renderCalculatorLootTableEditorAvailableList(
                nextState.editor,
                { provenanceDetailBindings: detailBindings },
            ),
        };
    }
    const regionFragments = new Map(
        [...regions.keys()].map((region) => [
            region,
            renderCalculatorLayerInspectorRegion(nextState, region),
        ]),
    );
    const rankingPresentation = impact.ranking
        ? renderCalculatorLayerInspectorRankingPresentation(nextState)
        : null;
    const focused = calculatorLootEditorFocusedControl(host);
    const scope = focusScope(host);
    const openStates = new Map([...regions].map(([region, element]) => [
        region,
        captureOpenDetails(element),
    ]));
    const provenanceScroll = regions.has("provenance")
        ? captureProvenanceScroll(regions.get("provenance"))
        : null;

    if (catalogFragment) {
        catalog.innerHTML = catalogFragment.markup;
        bindProvenanceDetailBindings(catalog, catalogFragment.detailBindings);
        catalogSummary.textContent = nextState.editor.availableSummary;
    }
    for (const [groupId, fragment] of groupFragments) {
        const element = groups.get(groupId);
        if (!reconcileFocusedRateGroupContent(element, fragment.markup)) {
            element.innerHTML = fragment.markup;
        }
        bindProvenanceDetailBindings(element, fragment.detailBindings);
    }
    if (rankingPresentation !== null) {
        rankingBody.innerHTML = rankingPresentation.body;
        rankingSummary.innerHTML = rankingPresentation.summary;
    }
    for (const [region, markup] of regionFragments) {
        const element = regions.get(region);
        element.innerHTML = markup;
        restoreOpenDetails(element, openStates.get(region));
        if (region === "provenance") {
            restoreProvenanceScroll(element, provenanceScroll);
        }
    }
    announcementElement.textContent = announcement;
    workspace.setAttribute(
        "data-loot-editor-has-connections",
        String(nextState.editor.hasSelectedConnections),
    );
    workspace.style?.setProperty?.(
        "--loot-editor-connection-color",
        nextState.editor.selectedConnectionColor,
    );
    bindCalculatorAssignmentConnections(workspace);

    const replacedFocusRoot = focusRoot(scope, {
        catalog,
        groups,
        rankingBody,
        regions,
    });
    if (replacedFocusRoot) {
        restoreCalculatorLootEditorFocusedControl(replacedFocusRoot, focused);
    }
    return true;
}
